const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK = 0x4e4f534a;
const BINARY_CHUNK = 0x004e4942;
/** The same focused parser is used by the runtime, private import validation and offline tools. */
export function parseStaticGlb(data, options = {}) {
    const view = new DataView(data);
    if (view.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC)
        fail("Invalid GLB header");
    if (view.getUint32(4, true) !== 2)
        fail("Only glTF 2.0 is supported");
    if (view.getUint32(8, true) !== view.byteLength)
        fail("GLB length mismatch");
    let document;
    let binary;
    for (let offset = 12; offset < view.byteLength;) {
        if (offset + 8 > view.byteLength)
            fail("Truncated GLB chunk header");
        const length = view.getUint32(offset, true);
        const type = view.getUint32(offset + 4, true);
        const start = offset + 8;
        const end = start + length;
        if (length === 0 || length % 4 !== 0 || end > view.byteLength)
            fail("Invalid GLB chunk length/alignment");
        if (type === JSON_CHUNK && offset === 12 && !document) {
            try {
                document = record(JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(new Uint8Array(data, start, length)), (_key, value) => {
                    if (typeof value === "number" && !Number.isFinite(value))
                        fail("Non-finite GLB JSON number");
                    return value;
                }), "glTF document");
            }
            catch {
                fail("Invalid GLB JSON document");
            }
        }
        else if (type === BINARY_CHUNK && document && !binary) {
            binary = data.slice(start, end);
        }
        else
            fail("Unsupported, duplicate or out-of-order GLB chunk");
        offset = end;
    }
    if (!document || !binary)
        fail("Incomplete glTF 2.0 asset");
    keys(document, ["asset", "scene", "scenes", "nodes", "meshes", "materials", "textures", "images", "samplers", "accessors", "bufferViews", "buffers", "extras"], "glTF document");
    const assetInfo = record(document.asset, "glTF asset");
    keys(assetInfo, ["version", "generator", "copyright", "extras"], "glTF asset");
    if (assetInfo.version !== "2.0")
        fail("Only glTF 2.0 is supported");
    const buffers = array(document.buffers, "buffers");
    if (buffers.length !== 1)
        fail("ENDCLOCK static assets require one embedded buffer");
    const buffer = record(buffers[0], "embedded buffer");
    keys(buffer, ["byteLength", "name", "extras"], "embedded buffer (external resources are unsupported)");
    const byteLength = integer(buffer.byteLength, "buffer byteLength", 1);
    if (byteLength > binary.byteLength || binary.byteLength - byteLength > 3)
        fail("Embedded buffer byteLength mismatch");
    if (new Uint8Array(binary, byteLength).some((byte) => byte !== 0))
        fail("Invalid GLB binary padding");
    const binaryView = new DataView(binary, 0, byteLength);
    const scenes = array(document.scenes, "scenes");
    const nodes = array(document.nodes, "nodes");
    const meshes = array(document.meshes, "meshes");
    if (scenes.length !== 1 || nodes.length !== 1 || meshes.length !== 1 || defaulted(document.scene, 0) !== 0) {
        fail("ENDCLOCK static assets require one scene, one node and one mesh");
    }
    const scene = record(scenes[0], "scene");
    keys(scene, ["nodes", "name", "extras"], "scene");
    if (!Array.isArray(scene.nodes) || scene.nodes.length !== 1 || scene.nodes[0] !== 0)
        fail("Static scene requires exactly node 0");
    const node = record(nodes[0], "node");
    keys(node, ["mesh", "name", "extras"], "static node (transforms, children and skins are unsupported)");
    if (node.mesh !== 0)
        fail("Static node requires mesh 0");
    const mesh = record(meshes[0], "mesh");
    keys(mesh, ["primitives", "name", "extras"], "static mesh");
    const sourcePrimitives = array(mesh.primitives, "primitives");
    if (sourcePrimitives.length < 1 || sourcePrimitives.length > 4)
        fail("ENDCLOCK static assets require one to four primitives");
    const bufferViews = array(document.bufferViews, "bufferViews").map((value) => {
        const input = record(value, "buffer view");
        keys(input, ["buffer", "byteOffset", "byteLength", "byteStride", "target", "name", "extras"], "buffer view");
        if (input.buffer !== 0)
            fail("Buffer view requires embedded buffer 0");
        const byteOffset = integer(defaulted(input.byteOffset, 0), "buffer view byteOffset");
        const length = integer(input.byteLength, "buffer view byteLength", 1);
        if (byteOffset + length > byteLength)
            fail("Buffer view is outside embedded buffer");
        const stride = input.byteStride === undefined ? undefined : integer(input.byteStride, "buffer view byteStride", 4);
        if (stride !== undefined && (stride > 252 || stride % 4 !== 0))
            fail("Invalid buffer view byteStride");
        if (input.target !== undefined && input.target !== 34962 && input.target !== 34963)
            fail("Unsupported buffer view target");
        return { byteOffset, byteLength: length, ...(stride === undefined ? {} : { byteStride: stride }), ...(input.target === undefined ? {} : { target: input.target }) };
    });
    const accessors = array(document.accessors, "accessors").map((value) => {
        const input = record(value, "accessor");
        keys(input, ["bufferView", "byteOffset", "componentType", "count", "type", "min", "max", "normalized", "name", "extras"], "accessor (sparse accessors are unsupported)");
        if (input.normalized !== undefined && input.normalized !== false)
            fail("Normalized accessors are unsupported");
        const bufferView = reference(bufferViews, input.bufferView, "accessor bufferView");
        const type = input.type;
        if (type !== "VEC3" && type !== "VEC2" && type !== "SCALAR")
            fail("Unsupported static mesh accessor type");
        const componentType = integer(input.componentType, "accessor componentType");
        if (type === "SCALAR" ? ![5121, 5123, 5125].includes(componentType) : componentType !== 5126)
            fail("Unsupported static mesh accessor component type");
        const count = integer(input.count, "accessor count", 1);
        const byteOffset = integer(defaulted(input.byteOffset, 0), "accessor byteOffset");
        const componentBytes = componentType === 5121 ? 1 : componentType === 5123 ? 2 : 4;
        const width = componentBytes * (type === "VEC3" ? 3 : type === "VEC2" ? 2 : 1);
        const stride = bufferView.byteStride ?? width;
        if (type === "SCALAR" && bufferView.byteStride !== undefined)
            fail("Index buffer views must be tightly packed");
        if (byteOffset % componentBytes !== 0 || (bufferView.byteOffset + byteOffset) % componentBytes !== 0 || stride < width)
            fail("Invalid accessor offset/alignment/stride");
        if (byteOffset + (count - 1) * stride + width > bufferView.byteLength)
            fail("glTF accessor is outside its buffer view");
        const elements = type === "VEC3" ? 3 : type === "VEC2" ? 2 : 1;
        for (const key of ["min", "max"])
            if (input[key] !== undefined)
                numericVector(input[key], elements, `accessor ${key}`);
        if (input.min && input.max && input.min.some((n, i) => n > input.max[i]))
            fail("Invalid accessor bounds");
        return { bufferView, byteOffset, componentType, count, type };
    });
    const textures = optionalArray(document.textures, "textures");
    const images = optionalArray(document.images, "images");
    const samplers = optionalArray(document.samplers, "samplers");
    if (options.allowExternalTextures === false && (textures.length || images.length || samplers.length)) {
        fail("External texture resources are not supported by imported assets in WS-025");
    }
    const parsedImages = images.map((value) => {
        const input = record(value, "texture image");
        keys(input, ["uri", "name", "extras"], "texture image (embedded images are unsupported)");
        if (typeof input.uri !== "string" || !/^[a-zA-Z0-9][a-zA-Z0-9_-]*\.png$/.test(input.uri))
            fail("Static textures require a safe relative PNG image URI");
        return input.uri;
    });
    const parsedSamplers = samplers.map((value) => {
        const input = record(value, "texture sampler");
        keys(input, ["magFilter", "minFilter", "wrapS", "wrapT", "name", "extras"], "texture sampler");
        if (defaulted(input.magFilter, 9729) !== 9728 || defaulted(input.minFilter, 9729) !== 9728)
            fail("ENDCLOCK card textures require nearest filtering");
        for (const key of ["wrapS", "wrapT"])
            if (input[key] !== undefined && input[key] !== 33071)
                fail("ENDCLOCK card textures require clamp-to-edge wrapping");
        return { magFilter: 9728, minFilter: 9728 };
    });
    const parsedTextures = textures.map((value) => {
        const input = record(value, "texture");
        keys(input, ["sampler", "source", "name", "extras"], "texture");
        return { uri: reference(parsedImages, input.source, "texture source"), ...reference(parsedSamplers, input.sampler, "texture sampler") };
    });
    const materials = optionalArray(document.materials, "materials").map((value) => parseMaterial(value, parsedTextures));
    const defaultMaterial = parseMaterial({}, parsedTextures);
    const primitives = sourcePrimitives.map((value) => {
        const input = record(value, "primitive");
        keys(input, ["attributes", "indices", "material", "mode", "extras"], "primitive (morph targets and extensions are unsupported)");
        if (defaulted(input.mode, 4) !== 4)
            fail("Static asset primitives must use indexed triangles");
        const attributes = record(input.attributes, "primitive attributes");
        keys(attributes, ["POSITION", "NORMAL", "TEXCOORD_0"], "primitive attributes");
        if (attributes.POSITION === undefined || attributes.NORMAL === undefined || input.indices === undefined)
            fail("Static asset requires POSITION, NORMAL and indices");
        const positions = readVector(reference(accessors, attributes.POSITION, "POSITION"), binaryView, 3);
        const normals = readVector(reference(accessors, attributes.NORMAL, "NORMAL"), binaryView, 3);
        const indices = readIndices(reference(accessors, input.indices, "indices"), binaryView);
        const vertexCount = positions.length / 3;
        if (positions.length !== normals.length || indices.length % 3 !== 0)
            fail("Invalid static mesh topology");
        if (vertexCount > 65_536)
            fail("Static mesh exceeds 16-bit vertex budget");
        if (indices.some((index) => index >= vertexCount))
            fail("Static mesh index exceeds vertex count");
        const material = input.material === undefined ? (materials[0] ?? defaultMaterial) : reference(materials, input.material, "primitive material");
        const texcoords = attributes.TEXCOORD_0 === undefined ? undefined : readVector(reference(accessors, attributes.TEXCOORD_0, "TEXCOORD_0"), binaryView, 2);
        if (texcoords && texcoords.length / 2 !== vertexCount)
            fail("Texture coordinate count mismatch");
        if (material.baseColorTexture && !texcoords)
            fail("Base-color textures require TEXCOORD_0");
        return { geometry: { positions, normals, indices, ...(texcoords ? { texcoords } : {}) }, material };
    });
    const extras = record(node.extras, "static asset metadata");
    keys(extras, ["stableAssetId", "category", "collision", "boundsCenterY", "boundsRadius", "activePrimitiveCount"], "static asset metadata");
    if (typeof extras.stableAssetId !== "string" || extras.stableAssetId.length > 64 || !/^[a-z][a-z0-9_]*$/.test(extras.stableAssetId))
        fail("Missing or invalid static stableAssetId metadata");
    if (extras.activePrimitiveCount !== undefined && integer(extras.activePrimitiveCount, "activePrimitiveCount") > primitives.length)
        fail("Invalid activePrimitiveCount");
    const boundsCenterY = finite(defaulted(extras.boundsCenterY, 0), "boundsCenterY");
    const boundsRadius = positive(extras.boundsRadius, "boundsRadius");
    return { id: extras.stableAssetId, primitives, collision: parseCollision(extras.collision), boundsCenterY, boundsRadius };
}
/** Imports provide only GLB + metadata, so no companion textures can be resolved. */
export function validateImportedStaticGlb(data) {
    const asset = parseStaticGlb(data, { allowExternalTextures: false });
    if (Math.abs(asset.boundsCenterY) > 10_000 || asset.boundsRadius > 10_000) {
        fail("Imported GLB bounds exceed the 10,000 meter authoring/runtime limit");
    }
    for (const primitive of asset.primitives) {
        const positions = primitive.geometry.positions;
        for (let i = 0; i < positions.length; i += 3) {
            if (Math.hypot(positions[i], positions[i + 1] - asset.boundsCenterY, positions[i + 2]) > asset.boundsRadius + 0.0001) {
                fail("Imported GLB bounds sphere does not contain its geometry");
            }
        }
    }
    return asset;
}
function parseMaterial(value, textures) {
    const input = record(value, "material");
    keys(input, ["pbrMetallicRoughness", "emissiveFactor", "alphaMode", "alphaCutoff", "doubleSided", "name", "extras"], "material");
    const pbr = input.pbrMetallicRoughness === undefined ? {} : record(input.pbrMetallicRoughness, "PBR material");
    keys(pbr, ["baseColorFactor", "baseColorTexture", "metallicFactor", "roughnessFactor"], "PBR material");
    if (pbr.metallicFactor !== undefined && pbr.metallicFactor !== 0)
        fail("Metallic material response is unsupported");
    if (pbr.roughnessFactor !== undefined && pbr.roughnessFactor !== 1)
        fail("Variable material roughness is unsupported");
    const baseColor = pbr.baseColorFactor === undefined ? [0.3, 0.28, 0.24, 1] : numericVector(pbr.baseColorFactor, 4, "baseColorFactor", true);
    const emissiveColor = input.emissiveFactor === undefined ? [0, 0, 0] : numericVector(input.emissiveFactor, 3, "emissiveFactor", true);
    const extras = input.extras === undefined ? {} : record(input.extras, "material extras");
    keys(extras, ["endclockEmissiveStrength", "endclockPulseStrength"], "material extras");
    const alphaMode = defaulted(input.alphaMode, "OPAQUE");
    if (alphaMode !== "OPAQUE" && alphaMode !== "MASK")
        fail("Only opaque and alpha-mask materials are supported");
    const alphaCutoff = unit(defaulted(input.alphaCutoff, 0.5), "alphaCutoff");
    if (input.doubleSided !== undefined && typeof input.doubleSided !== "boolean")
        fail("Invalid material doubleSided");
    let baseColorTexture;
    if (pbr.baseColorTexture !== undefined) {
        const info = record(pbr.baseColorTexture, "base-color texture");
        keys(info, ["index", "texCoord"], "base-color texture");
        if (defaulted(info.texCoord, 0) !== 0)
            fail("Base-color textures require TEXCOORD_0");
        baseColorTexture = reference(textures, info.index, "base-color texture index");
    }
    if (alphaMode === "MASK" && !baseColorTexture)
        fail("Alpha-mask material requires a base-color texture");
    return {
        baseColor: baseColor, emissiveColor: emissiveColor,
        emissiveStrength: unit(defaulted(extras.endclockEmissiveStrength, 0), "emissiveStrength"),
        pulseStrength: unit(defaulted(extras.endclockPulseStrength, 0), "pulseStrength"),
        alphaMode, alphaCutoff: alphaMode === "MASK" ? alphaCutoff : 0, doubleSided: input.doubleSided === true,
        ...(baseColorTexture ? { baseColorTexture } : {}),
    };
}
function parseCollision(value) {
    const input = record(value, "collision metadata");
    if (input.shape === "none") {
        keys(input, ["shape", "radius"], "collision metadata");
        if (input.radius !== undefined && input.radius !== 0)
            fail("Invalid non-colliding radius");
        return { shape: "none" };
    }
    if (input.shape === "circle") {
        keys(input, ["shape", "radius"], "collision metadata");
        return { shape: "circle", radius: positive(input.radius, "circle radius") };
    }
    if (input.shape === "box") {
        keys(input, ["shape", "halfWidth", "halfDepth"], "collision metadata");
        return { shape: "box", halfWidth: positive(input.halfWidth, "box half-width"), halfDepth: positive(input.halfDepth, "box half-depth") };
    }
    if (input.shape === "capsule") {
        keys(input, ["shape", "radius", "halfLength"], "collision metadata");
        return { shape: "capsule", radius: positive(input.radius, "capsule radius"), halfLength: positive(input.halfLength, "capsule half-length") };
    }
    return fail("Invalid collision metadata");
}
function readVector(accessor, binary, elements) {
    if (accessor.type !== `VEC${elements}` || accessor.componentType !== 5126)
        fail(`Static mesh requires FLOAT VEC${elements} accessor`);
    if (accessor.bufferView.target !== undefined && accessor.bufferView.target !== 34962)
        fail("Vector accessor requires vertex buffer target");
    const result = new Float32Array(accessor.count * elements);
    const start = accessor.bufferView.byteOffset + accessor.byteOffset;
    const stride = accessor.bufferView.byteStride ?? elements * 4;
    for (let i = 0; i < accessor.count; i += 1)
        for (let axis = 0; axis < elements; axis += 1) {
            const value = binary.getFloat32(start + i * stride + axis * 4, true);
            if (!Number.isFinite(value))
                fail("Static mesh contains non-finite numeric data");
            result[i * elements + axis] = value;
        }
    return result;
}
function readIndices(accessor, binary) {
    if (accessor.type !== "SCALAR")
        fail("Static indices require SCALAR accessor");
    if (accessor.bufferView.target !== undefined && accessor.bufferView.target !== 34963)
        fail("Index accessor requires index buffer target");
    const result = new Uint16Array(accessor.count);
    const bytes = accessor.componentType === 5121 ? 1 : accessor.componentType === 5123 ? 2 : 4;
    const start = accessor.bufferView.byteOffset + accessor.byteOffset;
    for (let i = 0; i < accessor.count; i += 1) {
        const offset = start + i * bytes;
        const value = bytes === 1 ? binary.getUint8(offset) : bytes === 2 ? binary.getUint16(offset, true) : binary.getUint32(offset, true);
        if (value > 65_535)
            fail("Static mesh exceeds 16-bit index budget");
        result[i] = value;
    }
    return result;
}
function record(value, label) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        fail(`Invalid ${label}: expected object`);
    return value;
}
function keys(input, allowed, label) {
    for (const key of Object.keys(input))
        if (!allowed.includes(key))
            fail(`Unsupported ${label} field: ${key}`);
}
function array(value, label) {
    if (!Array.isArray(value))
        fail(`Invalid ${label}: expected array`);
    return value;
}
function optionalArray(value, label) { return value === undefined ? [] : array(value, label); }
function finite(value, label) {
    if (typeof value !== "number" || !Number.isFinite(value))
        fail(`Invalid finite ${label}`);
    return value;
}
function integer(value, label, min = 0) {
    const result = finite(value, label);
    if (!Number.isSafeInteger(result) || result < min)
        fail(`Invalid integer ${label}`);
    return result;
}
function positive(value, label) {
    const result = finite(value, label);
    if (result <= 0)
        fail(`Invalid positive ${label}`);
    return result;
}
function unit(value, label) {
    const result = finite(value, label);
    if (result < 0 || result > 1)
        fail(`Invalid ${label}: expected value in [0, 1]`);
    return result;
}
function numericVector(value, size, label, bounded = false) {
    if (!Array.isArray(value) || value.length !== size)
        fail(`Invalid ${label}: expected ${size} numeric components`);
    return value.map((n) => bounded ? unit(n, label) : finite(n, label));
}
function reference(values, index, label) {
    const result = values[integer(index, label)];
    if (result === undefined)
        fail(`Missing ${label} reference`);
    return result;
}
function defaulted(value, fallback) { return value === undefined ? fallback : value; }
function fail(message) { throw new Error(message); }
